import { Release, ReleaseExportStatus, TuneCoreRelease } from 'types/releases';
import { ListItemTextProps } from 'app/components/ChakraTableList';
import { ViewOnTuneCoreLink } from 'app/components/Releases/ViewOnTuneCoreLink';

export const getLatestExportedReleaseLabel = ({
  shipment,
}: {
  shipment: Release['latestExportShipment'];
}) => {
  if (shipment?.status === ReleaseExportStatus.PROCESSING) {
    return 'Export in progress';
  }
  if (shipment?.status === ReleaseExportStatus.FAILURE) {
    return 'Failed to export';
  }
  if (shipment?.status === ReleaseExportStatus.QUOTA_EXCEEDED) {
    return 'Failed: Quota exceeded';
  }
  return '';
};

export const getLatestExportedTuneCoreReleaseLabel = (
  release: TuneCoreRelease
) => {
  const status = release.latestExport?.status;
  const secondaryTextProps = {
    fontSize: 'xs',
    'data-testid': 'export-recipient',
    color: 'textSubdued',
  } as ListItemTextProps['secondaryTextProps'];

  if (status === ReleaseExportStatus.PROCESSING) {
    return {
      secondary: 'Export in Progress',
      secondaryTextProps,
    } as ListItemTextProps;
  }

  if (
    status === 'SUCCESS' ||
    Boolean(
      release.latestExport?.tuneCore?.callbackResponse?.tuneCoreReleaseUrl
    )
  ) {
    return {
      secondary: (
        <ViewOnTuneCoreLink
          release={release}
          fontWeight={400}
          fontSize="xs"
          as="span"
        />
      ),
      secondaryTextProps,
    } as ListItemTextProps;
  }

  if (status === 'FAIL') {
    return {
      secondary: 'Export failed. Please retry',
      secondaryTextProps: {
        ...secondaryTextProps,
        color: 'R.80',
      },
    } as ListItemTextProps;
  }

  return {};
};

export const ACCESSOR_SIZE = 52;
export const OPTIONS_SIZE = 50;

const getColumnSize = (percent: number, totalWidth?: number) => {
  if (!totalWidth) {
    return undefined;
  }
  return (totalWidth - ACCESSOR_SIZE - OPTIONS_SIZE) * percent;
};

export const getColumns = (totalWidth?: number) => [
  {
    accessorKey: 'accessory',
    size: ACCESSOR_SIZE,
    accessory: true,
  },
  {
    accessorKey: 'title',
    header: 'Title',
    minSize: 62,
    sx: { pl: 0 },
    size: getColumnSize(0.25, totalWidth),
    enableResizing: true,
  },
  {
    accessorKey: 'updated',
    header: 'Last Modified',
    size: getColumnSize(0.25, totalWidth),
    minSize: 120,
    enableResizing: true,
  },
  {
    accessorKey: 'exported',
    header: 'Date Exported',
    size: getColumnSize(0.25, totalWidth),
    minSize: 120,
    enableResizing: true,
  },
  {
    accessorKey: 'releaseType',
    header: 'Release Type',
    size: getColumnSize(0.25, totalWidth),
  },
  {
    accessorKey: 'gap',
    size: 0,
  },
  {
    accessorKey: 'options',
    size: OPTIONS_SIZE,
    isOptions: true,
  },
];
